import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCare } from '../../contexts/CareContext'
import CareLogForm from '../care-log/CareLogForm'
import VitalsForm from '../vitals/VitalsForm'
import IncidentForm from '../incidents/IncidentForm'
import QuickAppointmentForm from '../appointments/QuickAppointmentForm'
import MedicationExtractor from '../medications/MedicationExtractor'

/**
 * Quick-add sheet launched from the floating action button.
 * Four entry points; two open forms in place, two navigate.
 */
export default function QuickAddSheet({ onClose, initial = null }) {
  const navigate = useNavigate()
  const { recipient } = useCare()
  // `initial` lets other screens deep-link straight into one action.
  const [form, setForm] = useState(initial) // 'care-log' | 'vitals' | 'incident' | 'appointment' | 'prescription'

  const ITEMS = [
    {
      key: 'care-log',
      emoji: '📝',
      label: 'Log care note',
      sub: 'Mood, meals, sleep, observations',
      run: () => setForm('care-log'),
    },
    {
      key: 'meds',
      emoji: '💊',
      label: 'Log medications',
      sub: "Tick off today's doses",
      run: () => {
        onClose()
        navigate('/medications?tab=today')
      },
    },
    {
      key: 'vitals',
      emoji: '🩺',
      label: 'Log vitals',
      sub: 'Blood pressure, fluids, weight, glucose',
      run: () => setForm('vitals'),
    },
    {
      key: 'incident',
      emoji: '⚠️',
      label: 'Log incident',
      sub: 'A fall, a near-miss, anything concerning',
      run: () => setForm('incident'),
    },
    {
      key: 'appointment',
      emoji: '📅',
      label: 'Add appointment',
      sub: 'Doctor, date, and who is driving',
      run: () => setForm('appointment'),
    },
    {
      key: 'prescription',
      emoji: '📷',
      label: 'Scan prescription',
      sub: 'Photograph a label — Ginie reads it',
      run: () => setForm('prescription'),
    },
  ]

  // A form takes over the sheet once chosen.
  if (form === 'care-log') {
    return (
      <div className="modal-scrim" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="modal-panel">
          <CareLogForm
            recipientId={recipient.id}
            onCancel={onClose}
            onDone={onClose}
          />
        </div>
      </div>
    )
  }
  if (form === 'vitals') {
    return (
      <div className="modal-scrim" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="modal-panel">
          <VitalsForm recipientId={recipient.id} onCancel={onClose} onDone={onClose} />
        </div>
      </div>
    )
  }
  if (form === 'incident') {
    return (
      <div className="modal-scrim" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="modal-panel">
          <IncidentForm recipientId={recipient.id} onCancel={onClose} onDone={onClose} />
        </div>
      </div>
    )
  }
  if (form === 'appointment') {
    return (
      <div className="modal-scrim" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="modal-panel">
          <QuickAppointmentForm recipientId={recipient.id} onCancel={onClose} onDone={onClose} />
        </div>
      </div>
    )
  }
  if (form === 'prescription') {
    return (
      <div className="modal-scrim" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="modal-panel">
          <MedicationExtractor onCancel={onClose} onDone={onClose} />
        </div>
      </div>
    )
  }

  return (
    <div
      className="sheet-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-label="Quick add"
    >
      <div className="sheet">
        <div className="sheet-grip" />
        {ITEMS.map((it) => (
          <button key={it.key} className="sheet-item" onClick={it.run}>
            <span className="emoji" aria-hidden>{it.emoji}</span>
            <span>
              {it.label}
              <span className="sub">{it.sub}</span>
            </span>
          </button>
        ))}
        <button
          className="btn btn-ghost btn-block"
          style={{ marginTop: 4 }}
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
